import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────
// Portable skills — downloadable instructions for an AI assistant
//
// BlogForger ships two markdown "skill" files a user can hand to their own
// assistant (Claude, ChatGPT, Cursor, a local model…):
//
//   · **content-generator** — generated from the shared block schemas by
//     scripts/generate-skill; teaches a model to emit blog/email JSON this
//     system accepts.
//   · **api-access** — hand-authored; teaches an agent to drive a workspace
//     through the `/api/v1` key-pair endpoints.
//
// A skill is a *file plus the explanation of how to use it*. Both travel
// together: the server reads the file's own front matter for the version and
// description, and sends the how-to copy (steps, tips, an example prompt)
// with the catalog — so the download button and the info panel beside it can
// never describe different things, and a deployment whose image is missing a
// skill says so instead of 404ing on download.
// ─────────────────────────────────────────────────────────────────

/**
 * How to use one skill, in the words the editor's info panel shows.
 *
 * Copy lives on the server (see apps/server/src/skills/catalog.ts) with the
 * file it describes, rather than in the UI component: a skill that changes
 * its steps changes them in one place, and the panel is rendered from
 * whatever the deployment actually has.
 */
export const SkillHowToSchema = z.object({
  /** One paragraph: what this skill is for. */
  intro: z.string(),
  /** Ordered setup/usage steps, already worded for display. */
  steps: z.array(z.string()),
  /** Practical advice — the "tips & all" half of the panel. */
  tips: z.array(z.string()),
  /** A prompt the user can copy into their assistant to try it immediately. */
  examplePrompt: z.string().optional(),
});
export type SkillHowTo = z.infer<typeof SkillHowToSchema>;

/**
 * One skill as the catalog reports it. Everything except `howTo` is derived
 * from the file itself (front matter, size), so a regenerated skill updates
 * the card without anyone editing the UI.
 */
export const SkillSummarySchema = z.object({
  /** Stable slug used in the download URL. */
  id: z.string(),
  name: z.string(),
  /** One line for the sidebar row. */
  summary: z.string(),
  /** False when the file is not part of this deployment (see `unavailableReason`). */
  available: z.boolean(),
  /** Why it cannot be downloaded — shown instead of the button. */
  unavailableReason: z.string().nullable(),
  /** Suggested filename for the download. */
  filename: z.string(),
  /** From the file's front matter; null when it carries none. */
  version: z.string().nullable(),
  author: z.string().nullable(),
  description: z.string().nullable(),
  /** Bytes of the markdown, and its line count. Null when unavailable. */
  sizeBytes: z.number().int().nullable(),
  lineCount: z.number().int().nullable(),
  howTo: SkillHowToSchema,
});
export type SkillSummary = z.infer<typeof SkillSummarySchema>;

export const SkillListSchema = z.object({ skills: z.array(SkillSummarySchema) });
export type SkillList = z.infer<typeof SkillListSchema>;

/** Human-readable size for a skill card ("33 KB"). */
export function formatSkillSize(bytes: number | null): string | null {
  if (bytes === null || !Number.isFinite(bytes)) return null;
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return kb < 100 ? `${kb.toFixed(1)} KB` : `${Math.round(kb)} KB`;
}
